import { AssignmentGroup } from './assignment-group.model';
import {
    Embedded,
    EmbeddedProject,
    EmbeddedUser,
} from './embed.model';
import { Project } from './project.model';

export enum TicketType {
    INCIDENT = 'INCIDENT',
    SERVICE_REQUEST = 'SERVICE_REQUEST',
    PROBLEM = 'PROBLEM'
}

export enum TicketStatus {
    UNKNOWN_STATUS = 'UNKNOWN_STATUS',
    NEW = 'NEW',
    IN_PROGRESS = 'IN_PROGRESS',
    PLANNED = 'PLANNED',
    PLANNING = 'PLANNING',
    AWAITING_CHANGE = 'AWAITING_CHANGE',
    AWAITING_CUSTOMER = 'AWAITING_CUSTOMER',
    AWAITING_THIRD_PARTY = 'AWAITING_THIRD_PARTY',
    RESOLVED = 'RESOLVED',
    CLOSED = 'CLOSED'
}

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: { fullName: string; avatar: string; email: string; isInternal: boolean };
    $assignee: { fullName: string; avatar: string; email: string; isInternal: boolean };
    projectName: string;
    $project: { id: string; name: string };
    isArchived: boolean;
    $status: {
        id: string;
        icon: string;
        color: string;
    };
    $type: {
        id: string;
        icon: string;
        color: string;
    };
}

export type EmbedTicket<T extends embed> = Ticket & Pick<EmbedProperties, T>;

export const configuration: { [K in embed]: { embed?: string; transform: (ticket: Ticket, embedded: Embedded) => EmbedProperties[K] } } = {
    $type: {
        transform: ({ type }): { id: string; icon: string; color: string } => {
            switch (type) {
                case TicketType.SERVICE_REQUEST:
                    return { id: type, icon: 'how_to_reg', color: '#2A67A5' };
                case TicketType.PROBLEM:
                    return { id: type, icon: 'warning', color: '#FFA600' };
                default:
                    return { id: type, icon: 'bug_report', color: '#c00017' };
            }
        }
    },
    $status: {
        transform: ({ status }): { id: string; icon: string; color: string } => {
            switch (status) {
                case TicketStatus.NEW:
                    return { id: status, icon: 'radio_button_unchecked', color: '#ccd2d7' };
                case TicketStatus.IN_PROGRESS:
                    return { id: status, icon: 'timelapse', color: '#1565c0' };
                case TicketStatus.AWAITING_CUSTOMER:
                    return { id: status, icon: 'access_time', color: '#f9a825' };
                default:
                    return { id: status, icon: 'check_circle', color: '#2e7d32' };
            }
        }
    },
    isArchived: {
        transform: ({ status }: Ticket): boolean => {
            return status === TicketStatus.CLOSED;
        }
    },
    $project: {
        embed: 'projects',
        transform: ({ project }: Ticket, { projects: embeddedProjects }: Embedded): EmbeddedProject => {
            const projectObj = embeddedProjects[project];
            return new EmbeddedProject(projectObj);
        }
    },
    projectName: {
        embed: 'projects',
        transform: ({ project }: Ticket, { projects: embeddedProjects }: Embedded): string => {
            return embeddedProjects[project] && embeddedProjects[project].name || '';
        }
    },
    $createdBy: {
        embed: 'users',
        transform: ({ createdBy }: Ticket, { users: embeddedUsers }: Embedded): EmbeddedUser => {
            const user = embeddedUsers[createdBy];
            return new EmbeddedUser(user);
        }
    },
    $assignee: {
        embed: 'users',
        transform: ({ assignee }: Ticket, { users: embeddedUsers }: Embedded): EmbeddedUser => {
            const user = embeddedUsers[assignee];
            return new EmbeddedUser(user);
        }
    },
};

export class Ticket {
    id?: string;
    subject?: string;
    description?: string;
    status?: TicketStatus;
    type?: TicketType;
    createdBy?: string;
    createdAt?: Date;
    updatedAt?: Date;
    updatedBy?: string;
    watchlist?: string[];
    assignee?: string;
    project?: string;
    assignmentGroup?: string;
    parent?: string;
    isPrivate?: boolean;
    priority?: TicketPriority;
    urgency?: TicketUrgency;
    impact?: TicketImpact;
    businessService?: string;
    request?: string;
    confidentialNotification?: boolean;
    inventoryList?: string[];
    environment?: string;
    securityTypes?: string[];
    openedAt?: Date;
    resolvedAt?: Date;
    tags?: string[];
    linkedTickets?: string[];

    public static toCustomTicket<T extends embed>(data: Ticket, embedded: Embedded, customFields: T[]): EmbedTicket<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return { ...data, ...customData } as EmbedTicket<T>;
    }

    public static toCustomTicketList<T extends embed>(data: Ticket[], embedded: Embedded, customFields: T[] = []): EmbedTicket<T>[] {
        return data.map((ticket) => this.toCustomTicket(ticket, embedded, customFields));
    }

}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type rawValue = any;

export const maxFileSize = 4194304; // 4MB = 4194304, 1MB = 1048576, in Bytes

export enum TicketPriority {
    UNKNOWN_PRIORITY = 'UNKNOWN_PRIORITY',
    URGENT = 'URGENT',
    HIGH = 'HIGH',
    NORMAL = 'NORMAL',
    LOW = 'LOW'
}

export enum TicketUrgency {
    UNKNOWN_URGENCY = 'UNKNOWN_URGENCY',
    MUST = 'MUST',
    STANDARD = 'STANDARD',
    SMALL = 'SMALL'
}

export class IncidentCreation {
    subject: string;
    description: string;
    impact: TicketImpact;
    files: File[];
    isPrivate: boolean;
    tags: string[];
    linkedTickets: string[];
    inventoryList: string[];
}

export enum TicketImpact {
    UNKNOWN_IMPACT = 'UNKNOWN_IMPACT',
    BLOCKING = 'BLOCKING',
    MAJOR = 'MAJOR',
    MINOR = 'MINOR'
}

export enum StatusTranslation {
    UNKNOWN_STATUS = 'Inconnu',
    NEW = 'Nouveau',
    IN_PROGRESS = 'En cours',
    PLANNED = 'Planifié',
    PLANNING = 'A planifier',
    AWAITING_CHANGE = 'En attente des changements',
    AWAITING_CUSTOMER = 'En attente du client',
    AWAITING_THIRD_PARTY = 'En attente d\'un tiers',
    RESOLVED = 'Résolu',
    CLOSED = 'Clos'
}

export enum TypeTranslation {
    INCIDENT = 'Incident',
    SERVICE_REQUEST = 'Demande',
    PROBLEM = 'Problème'
}

export enum ImpactTranslation {
    BLOCKING = 'Bloquant',
    MAJOR = 'Majeur',
    MINOR = 'Mineur'
}

export enum PriorityTranslation {
    UNKNOWN_PRIORITY = 'Inconnue',
    URGENT = 'P1 - Urgente',
    HIGH = 'P2 - Haute',
    NORMAL = 'P3 - Normale',
    LOW = 'P4 - Basse'
}

interface TicketWizard {
    isPrivate: boolean;
    subject: string;
    description: string;
    status: string;
    watchlist: string[];
    project: Project;
    assignmentGroup: AssignmentGroup;
}

export class Incident extends Ticket {
    declare impact: TicketImpact;

    public static toJson({ project, assignmentGroup, ...data }: TicketWizard & { impact: string }): Incident {
        return {
            isPrivate: data.isPrivate,
            subject: data.subject,
            description: data.description,
            status: TicketStatus[data.status],
            type: TicketType.INCIDENT,
            watchlist: data.watchlist || [],
            project: project && project.id,
            assignmentGroup: assignmentGroup && assignmentGroup.id,
            impact: TicketImpact[data.impact],
        };
    }
}

export class ServiceRequest extends Ticket {
    declare businessService: string;
    declare request: string;

    public static toJson({ project, assignmentGroup, ...data }: TicketWizard & { businessService: string; request: string }): ServiceRequest {
        return {
            isPrivate: data.isPrivate,
            subject: data.subject,
            description: data.description,
            type: TicketType.SERVICE_REQUEST,
            watchlist: data.watchlist || [],
            project: project && project.id,
            assignmentGroup: assignmentGroup && assignmentGroup.id,
            businessService: data.businessService,
            request: data.request,
        };
    }
}
